/**
 * PGSS-5456: recovery for a route chunk that fails to load.
 *
 * Route components are lazy (`React.lazy` + `import()`) and their chunk filenames are
 * content-hashed (`CalendarApp-itTShKRU.js`). A tab opened before a deploy still asks for the
 * OLD hashes, so the first navigation afterwards fetches a chunk that no longer exists.
 * `React.lazy` then REJECTS -- and Suspense catches a suspension, never a rejection -- so the
 * throw reaches the root and React unmounts the whole tree: a blank screen, nothing logged.
 *
 * That is why refreshing "fixes" it: a reload pulls a fresh index.html carrying current hashes.
 * Doing the reload automatically is only safe if it cannot loop, hence the one-shot guard.
 */

const RELOAD_AT_KEY = 'fuse.chunkReloadAt';

// Long enough that a genuinely broken deploy cannot spin, short enough that a second stale-chunk
// navigation later in the same session still recovers on its own.
const RELOAD_COOLDOWN_MS = 30 * 1000;

const CHUNK_ERROR_PATTERNS = [
  // Vite / modern browsers
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  // Safari
  'importing a module script failed',
  // Vite's CSS preload helper
  'unable to preload css',
  // webpack-era wording, still emitted by some browsers
  'loading chunk',
];

/**
 * A missing chunk is a *transport* failure, not an application bug, and only it may trigger an
 * automatic reload. Anything else must surface as a visible error instead -- reloading on a
 * render bug would hide the bug behind an endless refresh.
 */
export function isChunkLoadError(error) {
  if (!error) {
    return false;
  }

  if (error.name === 'ChunkLoadError') {
    return true;
  }

  const message = String(error.message || error).toLowerCase();

  return CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
}

/**
 * sessionStorage is per-tab, which is what we want: the guard should not leak across tabs, and it
 * should clear when the tab closes. It throws outright in some privacy modes, so every access is
 * guarded -- failing to read the guard must not stop the recovery, and failing to write it must
 * not stop the reload.
 */
function readLastReloadAt() {
  try {
    return Number(window.sessionStorage.getItem(RELOAD_AT_KEY)) || 0;
  } catch (e) {
    return 0;
  }
}

function writeLastReloadAt(at) {
  try {
    window.sessionStorage.setItem(RELOAD_AT_KEY, String(at));
  } catch (e) {
    /* storage unavailable -- proceed without the guard rather than not recovering at all */
  }
}

/**
 * Reload once, guarded. Returns true when a reload was actually started, so the caller can keep
 * rendering nothing instead of flashing an error panel the user will never finish reading.
 *
 * Callers that already know the failure is a missing chunk (Vite's `vite:preloadError`) use this
 * directly; callers holding an arbitrary thrown value go through reloadOnceForChunkError.
 */
export function reloadOnce() {
  const now = Date.now();

  // Already reloaded recently: the chunk is not merely stale, so stop and let the error be shown.
  // Without this a permanently missing chunk would refresh the tab forever.
  if (now - readLastReloadAt() < RELOAD_COOLDOWN_MS) {
    return false;
  }

  writeLastReloadAt(now);
  window.location.reload();

  return true;
}

/**
 * Reload once, but only for a stale chunk. Anything else must surface as a visible error.
 */
export function reloadOnceForChunkError(error) {
  return isChunkLoadError(error) ? reloadOnce() : false;
}

export default reloadOnceForChunkError;
